import { getMimeTypeFromFileName } from "@validereinc/utilities";
import { isEqual } from "es-toolkit/compat";
import React, { memo, useEffect, useRef } from "react";
import { getDisplayedErrorMessage } from "../../../utils/error";
import { FileCard } from "../FileCard";
import { useFileUploadInputContext } from "../FileUploadInputContext";
import type { FileUploadCardProps, FileUploadedRefType } from "../types";

const FileUploadCard = <T extends FileUploadedRefType = FileUploadedRefType>({
  id,
  file,
  fileUploaded,
  percentageProcessed,
  percentageProcessedLabel,
  error,
  abortController,
  startedProcessing,
  downloadFile,
  uploadFile,
  onChange,
  onCancel,
  onCancelResolved,
  isBusy,
  isValid,
}: FileUploadCardProps<T>) => {
  const { setFiles } = useFileUploadInputContext();

  // dedicated controller for the download flow so long-running scan-status
  // polling can be aborted when the card unmounts (e.g. the user navigates away)
  const downloadAbortControllerRef = useRef<AbortController | null>(null);

  if (downloadAbortControllerRef.current === null) {
    downloadAbortControllerRef.current = new AbortController();
  }

  useEffect(
    () => () => {
      downloadAbortControllerRef.current?.abort(
        "File preview unmounted; stopped polling for file scan status."
      );
      // stop any in-flight upload and its (potentially unbounded) scan-status
      // polling when the card unmounts, e.g. the user navigates away
      abortController.abort(
        "File upload card unmounted; stopped uploading and polling for file scan status."
      );
    },
    [abortController]
  );

  useEffect(() => {
    if (file || !fileUploaded || startedProcessing) return;

    setFiles((oldFiles) =>
      oldFiles.map((f) => {
        if (f.id === id) {
          f.startedProcessing = true;
          return f;
        }

        return f;
      })
    );

    // download the file
    downloadFile(fileUploaded, {
      setStatus: (newStatus) => {
        setFiles((oldFiles) =>
          oldFiles.map((f) => {
            if (f.id === id) {
              f.percentageProcessed = newStatus.percentComplete;
              f.percentageProcessedLabel = newStatus.label;
              f.error = newStatus.error;
              return f;
            }

            return f;
          })
        );
      },
      signal: downloadAbortControllerRef.current?.signal ?? null,
    })
      .then((f) => {
        if (!f) throw new Error("Failed to retrieve file.");

        onChange?.({
          id,
          fileUploaded,
          file: f,
          percentageProcessed: 100,
        });
      })
      .catch((err) => {
        // the card unmounted mid-download; end silently
        if (downloadAbortControllerRef.current?.signal.aborted) return;

        console.error("FileUploadCard: failed to download file.", err);
        onChange?.({
          id,
          fileUploaded,
          error: err,
        });
      });
  }, [fileUploaded, file, id, startedProcessing]);

  useEffect(() => {
    if (!file || fileUploaded || startedProcessing) return;

    setFiles((oldFiles) =>
      oldFiles.map((f) => {
        if (f.id === id) {
          f.startedProcessing = true;
          return f;
        }

        return f;
      })
    );

    // upload the file
    uploadFile(file, {
      setStatus: (newStatus) => {
        setFiles((oldFiles) =>
          oldFiles.map((f) => {
            if (f.id === id) {
              f.percentageProcessed = newStatus.percentComplete;
              f.percentageProcessedLabel = newStatus.label;
              f.error = newStatus.error;
              return f;
            }

            return f;
          })
        );
      },
      signal: abortController.signal,
    })
      .then((f) => {
        if (abortController.signal.aborted) {
          // user cancelled or the card unmounted, end silently
          return;
        }

        if (!f) throw new Error("Failed to get reference to uploaded file.");

        onChange?.({
          id,
          fileUploaded: f,
          file,
          percentageProcessed: 100,
        });
      })
      .catch((err) => {
        // the card unmounted or upload was cancelled mid-flight; end silently
        if (abortController.signal.aborted) return;

        console.error("FileUploadCard: failed to upload file.", err);
        onChange?.({
          id,
          file,
          error: err,
        });
      })
      .finally(() => {
        // clean up aborted file references
        if (abortController.signal.aborted) {
          onCancelResolved?.(id);
          return;
        }
      });
  }, [fileUploaded, file, id, startedProcessing]);

  const mimeType = getMimeTypeFromFileName(
    fileUploaded?.name ?? file?.name ?? ""
  );

  return (
    <FileCard
      file={
        file ??
        (fileUploaded?.name
          ? new File(
              [],
              fileUploaded.name,
              mimeType ? { type: mimeType } : undefined
            ) // placeholder file till the actual file is downloaded
          : undefined)
      }
      isCancellable={!!file && !fileUploaded}
      percentageComplete={percentageProcessed}
      percentageLabel={percentageProcessedLabel}
      errorMessage={getDisplayedErrorMessage(error)}
      isBusy={isBusy}
      isValid={isValid}
      onCancel={() => {
        abortController.abort("User cancelled the upload.");
        onCancel?.();
      }}
      onChange={(newFile) => {
        // handle delete
        if (!newFile) {
          onChange?.();
          return;
        }

        // handle update
        onChange?.({
          id,
          fileUploaded,
          file: newFile,
          percentageProcessed,
          error,
        });
      }}
    />
  );
};

// default exporting will drop typing
export const FileUploadCardMemoized = memo(FileUploadCard, isEqual) as <
  T extends FileUploadedRefType,
>(
  props: FileUploadCardProps<T>
) => JSX.Element;
